import React, { useEffect, useState, useCallback } from "react";
import {
  Tabs, Tab, Button, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Switch, FormControlLabel,
  Tooltip, Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { toast } from "react-toastify";
import AddIcon from "@material-ui/icons/Add";
import TuneIcon from "@material-ui/icons/Tune";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";
import api from "../../services/api";
import useConfirm from "../../hooks/useConfirm";

const useStyles = makeStyles((theme) => ({
  tabs: { flexBasis: "100%", minHeight: 40, marginBottom: theme.spacing(-0.5) },
  num: { fontVariantNumeric: "tabular-nums" },
  code: {
    fontFamily: "'SF Mono', 'Roboto Mono', Menlo, monospace",
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
}));

const ScoreRules = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const [tab, setTab] = useState(0);
  const [factors, setFactors] = useState([]);
  const [rules, setRules] = useState([]);
  const [factorDlg, setFactorDlg] = useState({ open: false, form: {} });
  const [ruleDlg, setRuleDlg] = useState({ open: false, editing: null, form: {} });

  const loadFactors = useCallback(async () => {
    try {
      const { data } = await api.get("/score/factors");
      setFactors(Array.isArray(data) ? data : []);
    } catch (err) { toast.error("Erro ao listar fatores"); }
  }, []);

  const loadRules = useCallback(async () => {
    try {
      const { data } = await api.get("/score/rules");
      setRules(Array.isArray(data) ? data : []);
    } catch (err) { toast.error("Erro ao listar regras"); }
  }, []);

  useEffect(() => { loadFactors(); loadRules(); }, [loadFactors, loadRules]);

  const saveFactor = async () => {
    try {
      const { id, ...payload } = factorDlg.form;
      await api.put(`/score/factors/${id}`, payload);
      toast.success("Fator atualizado");
      setFactorDlg({ open: false, form: {} });
      loadFactors();
    } catch (err) { toast.error("Erro ao salvar fator"); }
  };

  const saveRule = async () => {
    try {
      const f = ruleDlg.form;
      const payload = {
        name: f.name,
        factorId: Number(f.factorId),
        points: Number(f.points || 0),
        decayDays: Number(f.decayDays || 0),
        priority: Number(f.priority || 100),
        active: !!f.active,
        condition: f.conditionRaw ? JSON.parse(f.conditionRaw) : null
      };
      if (ruleDlg.editing) {
        await api.put(`/score/rules/${ruleDlg.editing}`, payload);
      } else {
        await api.post("/score/rules", payload);
      }
      toast.success("Regra salva");
      setRuleDlg({ open: false, editing: null, form: {} });
      loadRules();
    } catch (err) { toast.error("Erro ao salvar regra"); }
  };

  const removeRule = async (id) => {
    if (!(await confirm({ title: "Remover esta regra de score?", message: "Os scores já calculados não mudam até o próximo recálculo." }))) return;
    try {
      await api.delete(`/score/rules/${id}`);
      toast.success("Regra removida");
      loadRules();
    } catch (err) { toast.error("Erro ao remover"); }
  };

  const factorColumns = [
    { key: "key", label: "Chave", render: f => <code className={classes.code}>{f.key}</code> },
    { key: "name", label: "Nome", render: f => <Typography variant="subtitle2">{f.name}</Typography> },
    { key: "category", label: "Categoria", render: f => <StatusChip tone="neutral" label={f.category} /> },
    {
      key: "defaultPoints",
      label: "Pontos padrão",
      align: "right",
      render: f => <span className={classes.num}>{Number(f.defaultPoints).toFixed(2)}</span>,
    },
    {
      key: "active",
      label: "Situação",
      render: f => (
        <StatusChip dot tone={f.active ? "success" : "neutral"} label={f.active ? "Ativo" : "Inativo"} />
      ),
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: f => (
        <Tooltip title="Editar">
          <IconButton size="small" onClick={() => setFactorDlg({ open: true, form: { ...f } })}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const ruleColumns = [
    { key: "name", label: "Nome", render: r => <Typography variant="subtitle2">{r.name}</Typography> },
    { key: "factor", label: "Fator", render: r => r.factor?.name || `#${r.factorId}` },
    {
      key: "points", label: "Pontos", align: "right",
      render: r => <span className={classes.num}>{Number(r.points).toFixed(2)}</span>,
    },
    {
      key: "decayDays", label: "Decay (dias)", align: "right",
      render: r => (
        <span className={classes.num}>{r.decayDays > 0 ? r.decayDays : "—"}</span>
      ),
    },
    {
      key: "priority", label: "Prioridade", align: "right",
      render: r => <span className={classes.num}>{r.priority}</span>,
    },
    {
      key: "active",
      label: "Situação",
      render: r => (
        <StatusChip dot tone={r.active ? "success" : "neutral"} label={r.active ? "Ativa" : "Inativa"} />
      ),
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: r => (
        <>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              onClick={() => setRuleDlg({
                open: true,
                editing: r.id,
                form: {
                  ...r,
                  factorId: r.factorId,
                  conditionRaw: r.condition ? JSON.stringify(r.condition, null, 2) : ""
                }
              })}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton size="small" onClick={() => removeRule(r.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <MainContainer>
      {confirmDialog}
      <PageHeader
        title="Configuração de Score"
        subtitle="Fatores são os sinais que valem ponto; regras dizem quando e quantos pontos aplicar."
        actions={
          tab === 1 ? (
            <Button
              variant="contained" color="primary" startIcon={<AddIcon />}
              onClick={() => setRuleDlg({ open: true, editing: null, form: { active: true, priority: 100, decayDays: 0 } })}
            >
              Nova regra
            </Button>
          ) : null
        }
        filters={
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            indicatorColor="primary"
            textColor="primary"
            className={classes.tabs}
          >
            <Tab label={`Fatores (${factors.length})`} />
            <Tab label={`Regras (${rules.length})`} />
          </Tabs>
        }
      />

      {tab === 0 && (
        <DataTable
          columns={factorColumns}
          rows={factors}
          emptyIcon={<TuneIcon />}
          emptyTitle="Nenhum fator cadastrado"
          emptyDescription="Os fatores são criados pelo seed do sistema. Sem eles, nenhuma regra tem o que pontuar."
        />
      )}

      {tab === 1 && (
        <DataTable
          columns={ruleColumns}
          rows={rules}
          emptyIcon={<TuneIcon />}
          emptyTitle="Nenhuma regra cadastrada"
          emptyDescription="Enquanto não houver regra, nenhum cliente recebe pontuação automática."
          emptyAction={
            <Button
              variant="contained" color="primary" startIcon={<AddIcon />}
              onClick={() => setRuleDlg({ open: true, editing: null, form: { active: true, priority: 100, decayDays: 0 } })}
            >
              Nova regra
            </Button>
          }
        />
      )}

      {/* Factor edit */}
      <Dialog open={factorDlg.open} onClose={() => setFactorDlg({ open: false, form: {} })} maxWidth="sm" fullWidth>
        <DialogTitle>Editar Fator</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Chave" value={factorDlg.form.key || ""} disabled margin="dense" />
          <TextField
            fullWidth margin="dense" label="Nome" value={factorDlg.form.name || ""}
            onChange={e => setFactorDlg(d => ({ ...d, form: { ...d.form, name: e.target.value } }))}
          />
          <TextField
            fullWidth margin="dense" label="Descrição" value={factorDlg.form.description || ""}
            onChange={e => setFactorDlg(d => ({ ...d, form: { ...d.form, description: e.target.value } }))}
          />
          <TextField
            fullWidth margin="dense" label="Categoria" value={factorDlg.form.category || ""}
            onChange={e => setFactorDlg(d => ({ ...d, form: { ...d.form, category: e.target.value } }))}
          />
          <TextField
            fullWidth margin="dense" type="number" label="Pontos padrão"
            value={factorDlg.form.defaultPoints ?? 0}
            onChange={e => setFactorDlg(d => ({ ...d, form: { ...d.form, defaultPoints: e.target.value } }))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!factorDlg.form.active}
                onChange={e => setFactorDlg(d => ({ ...d, form: { ...d.form, active: e.target.checked } }))}
              />
            }
            label="Ativo"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFactorDlg({ open: false, form: {} })}>Cancelar</Button>
          <Button onClick={saveFactor} color="primary" variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Rule edit */}
      <Dialog open={ruleDlg.open} onClose={() => setRuleDlg({ open: false, editing: null, form: {} })} maxWidth="sm" fullWidth>
        <DialogTitle>{ruleDlg.editing ? "Editar Regra" : "Nova Regra"}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth margin="dense" label="Nome"
            value={ruleDlg.form.name || ""}
            onChange={e => setRuleDlg(d => ({ ...d, form: { ...d.form, name: e.target.value } }))}
          />
          <TextField
            select fullWidth margin="dense" label="Fator"
            value={ruleDlg.form.factorId || ""}
            onChange={e => setRuleDlg(d => ({ ...d, form: { ...d.form, factorId: e.target.value } }))}
          >
            {factors.map(f => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
          </TextField>
          <TextField
            fullWidth margin="dense" type="number" label="Pontos"
            value={ruleDlg.form.points ?? 0}
            onChange={e => setRuleDlg(d => ({ ...d, form: { ...d.form, points: e.target.value } }))}
          />
          <TextField
            fullWidth margin="dense" type="number" label="Decay (dias)"
            value={ruleDlg.form.decayDays ?? 0}
            onChange={e => setRuleDlg(d => ({ ...d, form: { ...d.form, decayDays: e.target.value } }))}
          />
          <TextField
            fullWidth margin="dense" type="number" label="Prioridade"
            value={ruleDlg.form.priority ?? 100}
            onChange={e => setRuleDlg(d => ({ ...d, form: { ...d.form, priority: e.target.value } }))}
          />
          <TextField
            fullWidth margin="dense" multiline minRows={3}
            label='Condição (JSON, ex.: {"op":"gte","value":5000})'
            value={ruleDlg.form.conditionRaw || ""}
            onChange={e => setRuleDlg(d => ({ ...d, form: { ...d.form, conditionRaw: e.target.value } }))}
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!ruleDlg.form.active}
                onChange={e => setRuleDlg(d => ({ ...d, form: { ...d.form, active: e.target.checked } }))}
              />
            }
            label="Ativo"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRuleDlg({ open: false, editing: null, form: {} })}>Cancelar</Button>
          <Button onClick={saveRule} color="primary" variant="contained">Salvar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default ScoreRules;
